import { useEffect } from "react";
import { useSendLoyaltyEventRecordMutation } from "Api/Mutations/Loyalty/useSendLoyaltyEventRecordMutation";
import {
  canSendDailyEvent,
  setAnyDailyEventToStorage,
} from "Components/Loyalty/LoyaltyUtils";
import { useCurrentUserRole } from "Hooks/useCurrentUserAccessRole";
import { useHasStardustConsent } from "Hooks/useHasStardustConsent";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { LoyaltyEventCode } from "Infrastructure/Api/Api";
import { getApiErrorStatus } from "Infrastructure/Api/ApiUtils";
import { type AccessRoleCode, AccessRoleCodes } from "Models/AccessRoleCodes";

// Answers that stay the same for the rest of the day: Conflict = already counted, Forbidden =
// the account cannot earn. Only these mark the day as done — anything else (network, 5xx, 401,
// 429, …) is retried on the next resume. 401 must not mark it: the logout it triggers clears
// sessionStorage BEFORE this handler runs, so a mark written here would outlive the re-login.
const FINAL_ERROR_STATUSES = [403, 409];

const isFinalError = (error: unknown): boolean => {
  const status = getApiErrorStatus(error);

  return status !== undefined && FINAL_ERROR_STATUSES.includes(status);
};

// The backend dedup is check-then-insert — never let two requests for the same event race.
// A key stays in the set until ITS request settles (mutateAsync settles every call, unlike
// per-call mutate callbacks, which TanStack drops for superseded calls), so a context switch
// mid-request still sends the new role's event but never a second one for the same key.
// Module-level on purpose: it must survive the tracker remounting across route changes.
const inFlightEventKeys = new Set<string>();

const getDailyAppOpenEventCode = (
  userRole: AccessRoleCode | null,
): LoyaltyEventCode | null => {
  if (
    userRole === AccessRoleCodes.Hunter ||
    userRole === AccessRoleCodes.HunterPlus
  ) {
    return LoyaltyEventCode.HunterDailyAppOpen;
  }

  if (userRole === AccessRoleCodes.Customer) {
    return LoyaltyEventCode.CustomerDailyAppOpen;
  }

  return null;
};

// Only DailyAppOpenTracker calls this — AuthenticatedRoute renders it once the user passed all
// its gates. Mobile shells (Android TWA, iOS Capacitor) mostly resume the app from the
// background instead of relaunching it, so a mount-only trigger would miss every day the user
// just wakes the app up — the event is also sent whenever the page becomes visible again.
// visibilitychange is plain web API, so it covers the TWA (a Chrome tab, no Capacitor runtime),
// the iOS WKWebView and the desktop web alike. A non-final failure leaves no storage mark, so
// the next resume doubles as the retry.
export const useDailyAppOpenEvent = () => {
  const userRole = useCurrentUserRole();
  const user = useLoggedInUser();
  // ZS-1624: undefined until user-info is fetched; the event waits, exactly like it waits for the
  // region below.
  const { hasStardustConsent } = useHasStardustConsent();
  // Fire-and-forget gamification — failures only reach Sentry (MutationCache), never the user.
  const { mutateAsync: sendLoyaltyEvent } = useSendLoyaltyEventRecordMutation();

  useEffect(() => {
    const partyPublicID = user?.partyPublicID;
    const eventCode = getDailyAppOpenEventCode(userRole);

    // ZS-1624: a Customer without Stardust consent earns nothing — the backend would answer 403,
    // which is FINAL for the day below, so a person who consents later today would lose today's
    // open. Wait until user-info says OptedIn instead: the participation mutation invalidates it,
    // so consenting re-fires this effect. Valor (hunters) needs no consent.
    const isWaitingForStardustConsent =
      userRole === AccessRoleCodes.Customer && hasStardustConsent !== true;

    // Hold the loyalty event until the active context has a region — RequireRegionMiddleware would
    // otherwise reject this POST (403). The regionRequired dependency re-fires it once the gate clears.
    if (
      !partyPublicID ||
      user?.regionRequired ||
      !eventCode ||
      isWaitingForStardustConsent
    ) {
      return;
    }

    const eventKey = `${eventCode}:${partyPublicID}`;

    const sendIfDue = async () => {
      if (
        inFlightEventKeys.has(eventKey) ||
        document.visibilityState !== "visible" ||
        !canSendDailyEvent(eventCode, partyPublicID)
      ) {
        return;
      }

      inFlightEventKeys.add(eventKey);
      try {
        await sendLoyaltyEvent({ eventCode });
        setAnyDailyEventToStorage(eventCode, partyPublicID);
      } catch (error) {
        // A final answer (Conflict when the backend already counted today's open in another
        // tab or session, Forbidden when the account cannot earn) is remembered for the day
        // too — otherwise every resume would repeat a request that cannot succeed.
        if (isFinalError(error)) {
          setAnyDailyEventToStorage(eventCode, partyPublicID);
        }
      } finally {
        inFlightEventKeys.delete(eventKey);
      }
    };

    void sendIfDue();
    document.addEventListener("visibilitychange", sendIfDue);

    return () => document.removeEventListener("visibilitychange", sendIfDue);
  }, [
    userRole,
    user?.partyPublicID,
    user?.regionRequired,
    hasStardustConsent,
    sendLoyaltyEvent,
  ]);
};
